import { k } from '../../localization/t';

export const ELEMENT_ORDER = ['Water', 'Earth', 'Fire', 'Air'];
const TYPE_ORDER = ['Minion', 'Magic', 'Aura', 'Artifact'];
const RARITY_ORDER = ['Ordinary', 'Exceptional', 'Elite', 'Unique'];

/**
 * Sorcery-specific palette for charts. Picked to echo the game's element
 * identity (blue water / green earth / red fire / gold air) without
 * clashing with the medieval parchment-gold deck editor chrome.
 */
export const SORCERY_CHART_COLORS = {
  Water: '#4FA8D8',
  Earth: '#7AA06A',
  Fire: '#C85C4A',
  Air: '#D9C56A',
  Neutral: '#B7945A',
};

export const ELEMENT_FILLS = {
  Water: SORCERY_CHART_COLORS.Water,
  Earth: SORCERY_CHART_COLORS.Earth,
  Fire: SORCERY_CHART_COLORS.Fire,
  Air: SORCERY_CHART_COLORS.Air,
};

const CHART_PALETTE = [
  SORCERY_CHART_COLORS.Water,
  SORCERY_CHART_COLORS.Earth,
  SORCERY_CHART_COLORS.Fire,
  SORCERY_CHART_COLORS.Air,
  SORCERY_CHART_COLORS.Neutral,
  '#8A6FB8',
];

const THRESHOLD_FIELDS = {
  Water: 'waterThreshold',
  Earth: 'earthThreshold',
  Fire: 'fireThreshold',
  Air: 'airThreshold',
};

/**
 * Real Sorcery keywords (confirmed against the card database). We track the
 * ones that reveal a deck's character: speed, evasion, movement, setup.
 */
export const KEYWORD_DEFS = [
  { key: 'Genesis', label: 'Genesis', description: 'Enters-play trigger' },
  { key: 'Deathrite', label: 'Deathrite', description: 'Dies-triggered ability' },
  { key: 'Spellcaster', label: 'Spellcaster', description: 'Can cast magic' },
  { key: 'Airborne', label: 'Airborne', description: 'Flies on the surface' },
  { key: 'Voidwalk', label: 'Voidwalk', description: 'Survives in the void' },
  { key: 'Burrowing', label: 'Burrowing', description: 'Summoned underground' },
  { key: 'Waterbound', label: 'Waterbound', description: 'Requires water' },
  { key: 'Stealth', label: 'Stealth', description: 'Untargetable by opponents' },
  { key: 'Lethal', label: 'Lethal', description: 'Any damage kills a minion' },
  { key: 'Charge', label: 'Charge', description: 'No summoning sickness' },
  { key: 'Ranged', label: 'Ranged', description: 'Strikes at distance' },
  { key: 'Immobile', label: 'Immobile', description: 'Cannot move' },
];

export function getCardZone(entry) {
  if (entry?.zone) return entry.zone;
  const category = entry?.card?._sorceryCategory;
  if (category === 'Site') return 'atlas';
  if (category === 'Avatar') return 'avatar';
  if (category === 'Spell') return 'spellbook';
  return '';
}

export function getCardElements(card) {
  const elements = card?.elements;
  if (!Array.isArray(elements)) return [];
  return elements.map((el) => (typeof el === 'string' ? { id: el.toLowerCase(), name: el } : el));
}

export function getSorceryRarityLabel(rarity) {
  return String(rarity || '').trim() || 'Ordinary';
}

function incrementCount(map, label, value = 1) {
  if (!label) return;
  map.set(label, (map.get(label) || 0) + value);
}

function formatAverage(total, count) {
  return count === 0 ? '0.00' : (total / count).toFixed(2);
}

function buildBarChartData(sourceMap, fillSelector, sortFn) {
  return [...sourceMap.entries()]
    .sort(sortFn)
    .map(([label, value], index) => ({ label, value, fill: fillSelector(label, index) }));
}

function orderIndexOf(array, label) {
  const idx = array.indexOf(label);
  return idx === -1 ? array.length : idx;
}

function buildRarityChartData(sourceMap) {
  return buildBarChartData(
    sourceMap,
    (_label, index) => CHART_PALETTE[index % CHART_PALETTE.length],
    (a, b) => orderIndexOf(RARITY_ORDER, a[0]) - orderIndexOf(RARITY_ORDER, b[0])
  );
}

/**
 * Split a card's element identity into fractional shares that sum to 1.
 * Multi-element cards are counted equally across their elements, so a
 * dual-Water/Air minion contributes 0.5 to each bar in the stacked curve
 * instead of hiding its second element under an arbitrary "first".
 * Colorless cards go entirely to Neutral.
 */
export function getCardElementShares(card) {
  const els = getCardElements(card);
  const valid = els
    .map((el) => el?.name)
    .filter((name) => name && SORCERY_CHART_COLORS[name]);
  if (valid.length === 0) return [['Neutral', 1]];
  const share = 1 / valid.length;
  return valid.map((name) => [name, share]);
}

function countKeywords(card) {
  const text = String(card?.functional_text || card?.rulesText || '');
  const hits = {};
  for (const def of KEYWORD_DEFS) {
    const rx = new RegExp(`\\b${def.key}\\b`);
    if (rx.test(text)) hits[def.key] = 1;
  }
  return hits;
}

function costBucketLabel(cost) {
  if (cost >= 7) return '7+';
  return String(cost);
}

function powerBucketLabel(power) {
  if (power >= 6) return '6+';
  return String(power);
}

export function buildSorceryDeckMetrics(chosenCards) {
  const entries = Array.isArray(chosenCards) ? chosenCards : [];
  const spellbookEntries = entries.filter((e) => getCardZone(e) === 'spellbook');
  const atlasEntries = entries.filter((e) => getCardZone(e) === 'atlas');
  const avatarEntry = entries.find((e) => getCardZone(e) === 'avatar');

  const elementCounts = new Map();
  const costCounts = new Map();
  const typeCounts = new Map();
  const rarityCounts = new Map();
  const subtypeCounts = new Map();
  const powerCounts = new Map();
  const keywordCounts = new Map();

  // Stacked cost: Map<costLabel, Map<elementKey, fractional count>>.
  // Fractional because multi-element spells split their share evenly
  // across their elements.
  const costByElement = new Map();

  const thresholdDemand = {};
  const thresholdSupply = {};
  const deckElements = new Set();

  let totalCost = 0;
  let costCount = 0;
  let minionCount = 0;

  for (const entry of spellbookEntries) {
    const card = entry?.card;
    if (!card) continue;

    for (const el of getCardElements(card)) {
      const name = el?.name;
      if (name) {
        incrementCount(elementCounts, name);
        if (SORCERY_CHART_COLORS[name]) deckElements.add(name);
      }
    }

    const elementShares = getCardElementShares(card);

    const cost = Number(card.cost);
    if (Number.isFinite(cost)) {
      totalCost += cost;
      costCount += 1;
      const bucket = costBucketLabel(Math.max(0, Math.floor(cost)));
      incrementCount(costCounts, bucket);
      if (!costByElement.has(bucket)) costByElement.set(bucket, new Map());
      const bucketMap = costByElement.get(bucket);
      for (const [element, share] of elementShares) {
        incrementCount(bucketMap, element, share);
      }
    }

    const type = card.type;
    if (type) incrementCount(typeCounts, type);

    const rarity = getSorceryRarityLabel(card.rarity);
    incrementCount(rarityCounts, rarity);

    for (const [element, field] of Object.entries(THRESHOLD_FIELDS)) {
      const demand = Number(card[field] || 0);
      if (demand > 0) {
        thresholdDemand[element] = Math.max(thresholdDemand[element] || 0, demand);
        deckElements.add(element);
      }
    }

    const traits = Array.isArray(card.traits) ? card.traits : [];
    if (type === 'Minion') {
      minionCount += 1;
      for (const trait of traits) incrementCount(subtypeCounts, trait);
      const power = Number(card.power);
      if (Number.isFinite(power)) {
        incrementCount(powerCounts, powerBucketLabel(Math.max(0, Math.floor(power))));
      }
    }

    const kw = countKeywords(card);
    for (const [k, v] of Object.entries(kw)) incrementCount(keywordCounts, k, v);
  }

  let atlasMultiElement = 0;
  let atlasUtility = 0;
  // Per-element breakdown: `atlasSitesByElement` counts sites that
  // supply ≥1 pip of each element (a dual site counts toward both),
  // `atlasPipsByElement` sums the actual pip counts. Pip count can
  // exceed site count when a site provides 2+ pips of the same element.
  const atlasSitesByElement = { Water: 0, Earth: 0, Fire: 0, Air: 0 };
  const atlasPipsByElement = { Water: 0, Earth: 0, Fire: 0, Air: 0 };
  const atlasRarityCounts = new Map();
  for (const entry of atlasEntries) {
    const card = entry?.card;
    if (!card) continue;

    incrementCount(atlasRarityCounts, getSorceryRarityLabel(card.rarity));

    let suppliedElements = 0;
    let thresholdTotal = 0;
    for (const [element, field] of Object.entries(THRESHOLD_FIELDS)) {
      const t = Number(card[field] || 0);
      if (t > 0) {
        suppliedElements += 1;
        thresholdTotal += t;
        atlasSitesByElement[element] += 1;
        atlasPipsByElement[element] += t;
        // Keep the legacy "thresholdSupply" shape in sync. Other
        // consumers (threshold reach math) still read this.
        thresholdSupply[element] = (thresholdSupply[element] || 0) + 1;
      }
    }

    if (suppliedElements > 1) atlasMultiElement += 1;
    if (thresholdTotal === 0) atlasUtility += 1;
  }

  // Ensure all 4 element rows appear even at 0.
  const elementDataMap = new Map(elementCounts);
  for (const el of ELEMENT_ORDER) {
    if (!elementDataMap.has(el)) elementDataMap.set(el, 0);
  }

  // Seed both rarity series with every rarity so the spellbook and atlas
  // charts share the same rows in the same order. The panel pairs them
  // by label to show the two spreads side by side.
  const rarityDataMap = new Map(rarityCounts);
  const atlasRarityDataMap = new Map(atlasRarityCounts);
  for (const rarity of RARITY_ORDER) {
    if (!rarityDataMap.has(rarity)) rarityDataMap.set(rarity, 0);
    if (!atlasRarityDataMap.has(rarity)) atlasRarityDataMap.set(rarity, 0);
  }

  const costStacked = [...costCounts.keys()]
    .sort((a, b) => {
      if (a === '7+') return 1;
      if (b === '7+') return -1;
      return Number(a) - Number(b);
    })
    .map((bucket) => {
      const perElement = costByElement.get(bucket) || new Map();
      const row = { label: bucket };
      let total = 0;
      let topElement = null;
      for (const el of [...ELEMENT_ORDER, k('Neutral')]) {
        // Fractional shares mean stacks may end up at x.5. Rounding
        // here would drift totals across 7 buckets; keep the exact
        // float and let the tooltip show one decimal.
        row[el] = perElement.get(el) || 0;
        total += row[el];
        // Last non-zero element in render order is the topmost stack
        // segment. Used by the chart to round only the top of each bar.
        if (row[el] > 0) topElement = el;
      }
      row.total = total;
      row._topElement = topElement;
      return row;
    });

  const sortedDeckElements = ELEMENT_ORDER.filter((el) => deckElements.has(el));

  return {
    totals: {
      spellbookSize: spellbookEntries.length,
      atlasSize: atlasEntries.length,
      minionCount,
    },
    averages: {
      cost: formatAverage(totalCost, costCount),
      costNumeric: costCount === 0 ? 0 : totalCost / costCount,
    },
    thresholds: {
      demand: thresholdDemand,
      supply: thresholdSupply,
    },
    deckElements: sortedDeckElements,
    avatar: avatarEntry?.card || null,
    atlas: {
      total: atlasEntries.length,
      multiElement: atlasMultiElement,
      utility: atlasUtility,
      sitesByElement: atlasSitesByElement,
      pipsByElement: atlasPipsByElement,
    },
    keywords: KEYWORD_DEFS.map((def) => ({
      key: def.key,
      label: def.label,
      description: def.description,
      count: keywordCounts.get(def.key) || 0,
    })).filter((k) => k.count > 0),
    charts: {
      elements: buildBarChartData(
        elementDataMap,
        (label) => ELEMENT_FILLS[label] || CHART_PALETTE[0],
        (a, b) => orderIndexOf(ELEMENT_ORDER, a[0]) - orderIndexOf(ELEMENT_ORDER, b[0])
      ),
      cost: buildBarChartData(
        costCounts,
        (_label, index) => CHART_PALETTE[index % CHART_PALETTE.length],
        (a, b) => {
          if (a[0] === '7+') return 1;
          if (b[0] === '7+') return -1;
          return Number(a[0]) - Number(b[0]);
        }
      ),
      costStacked,
      power: buildBarChartData(
        powerCounts,
        () => SORCERY_CHART_COLORS.Neutral,
        (a, b) => {
          if (a[0] === '6+') return 1;
          if (b[0] === '6+') return -1;
          return Number(a[0]) - Number(b[0]);
        }
      ),
      types: buildBarChartData(
        typeCounts,
        (label) => {
          const idx = TYPE_ORDER.indexOf(label);
          return CHART_PALETTE[(idx === -1 ? 0 : idx) % CHART_PALETTE.length];
        },
        (a, b) => orderIndexOf(TYPE_ORDER, a[0]) - orderIndexOf(TYPE_ORDER, b[0])
      ).filter((e) => e.value > 0),
      subtypes: buildBarChartData(
        subtypeCounts,
        (_label, index) => CHART_PALETTE[index % CHART_PALETTE.length],
        (a, b) => b[1] - a[1]
      ).slice(0, 6),
      rarity: buildRarityChartData(rarityDataMap),
      rarityAtlas: buildRarityChartData(atlasRarityDataMap),
    },
  };
}
